import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useLocation } from 'react-router-dom';

import {
  PDP_CUSTOMIZE_TAB_ID,
  PDP_READYTOSHIP_TAB_ID,
} from 'commons/constants';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import useSetPdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/useSetPdpSelectedCustomizerTab';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import PDPShopTabsBarUI from './PDPShopTabsBarUI';
import usePDPOptions from '../state/context/usePDPOptions';
import usePDPStockAvailability from '../state/context/usePDPStockAvailability';

const PDPShopTabsBarInner = ({ isEditOrder = false }) => {
  const [hideShopsTabBar, setHideShopTabsBar] = useState(false);
  const location = useLocation();
  const showReadyToShipTab = location.search?.includes('readyToShip');

  const {
    loading: optionsLoading,
    data: { stock, options, upholsteryOptionId, hasSingleOptionType },
  } = usePDPOptions();

  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();
  const setPdpSelectedCustomizerTab = useSetPdpSelectedCustomizerTab();
  const { data: basicDetails } = usePDPBasicDetails();

  const {
    loading: availabilityLoading,
    data: {
      isEFGInternal,
      isMarkedOOSOrDiscontinued,
      isDiscontinued,
      isOutOfStock,
      isStockLoading,
    },
  } = usePDPStockAvailability();

  const price = basicDetails?.meta?.price;

  useEffect(() => {
    if (availabilityLoading || optionsLoading) {
      return;
    }

    if (isEmpty(options)) {
      setHideShopTabsBar(true);
      return;
    }
    if (showReadyToShipTab) {
      setPdpSelectedCustomizerTab(PDP_READYTOSHIP_TAB_ID);
    }

    if (isMarkedOOSOrDiscontinued && !isOutOfStock && !isDiscontinued) {
      setHideShopTabsBar(true);
      setPdpSelectedCustomizerTab(PDP_READYTOSHIP_TAB_ID);
      return;
    }

    if (!isEFGInternal) {
      setHideShopTabsBar(false);
      if (!showReadyToShipTab) {
        setPdpSelectedCustomizerTab(PDP_CUSTOMIZE_TAB_ID);
      }
      return;
    }

    if (pdpSelectedCustomizerTab !== PDP_READYTOSHIP_TAB_ID) {
      setPdpSelectedCustomizerTab(PDP_READYTOSHIP_TAB_ID);
    }

    if (hasSingleOptionType) {
      setHideShopTabsBar(true);
      return;
    }
    if (options?.length) {
      setHideShopTabsBar(false);
    }
    // TODO: Investigate and refactor this code to allow all dependencies to be
    // tracked once PDP state hooks have been refactored and improved. Adding
    // all dependencies right now appears to cause an infinite render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasSingleOptionType,
    isDiscontinued,
    isEFGInternal,
    isMarkedOOSOrDiscontinued,
    isOutOfStock,
    options,
    optionsLoading,
    showReadyToShipTab,
  ]);

  return (
    <PDPShopTabsBarUI
      hideShopsTabBar={hideShopsTabBar}
      isEFGInternal={isEFGInternal}
      loading={optionsLoading || (isMarkedOOSOrDiscontinued && isStockLoading)}
      price={price}
      showDecideLater={!isEditOrder && !!upholsteryOptionId}
      showReadyToShip={stock?.showReadyToShip}
    />
  );
};

PDPShopTabsBarInner.propTypes = {
  isEditOrder: PropTypes.bool,
};

const PDPShopTabsBar = props => (
  <SuspenseWithLoader fallback={<PDPShopTabsBarUI loading />}>
    <PDPShopTabsBarInner {...props} />
  </SuspenseWithLoader>
);

export default PDPShopTabsBar;
